/**
 * Tutorial PDF export utilities.
 *
 * Generates two PDFs:
 *   - exportTutorialPDF()  → cover + TOC + 11 chapters + formula annex + page footer
 *   - exportCertificate()  → A4 landscape, double gold border, fictitious seal
 *
 * Both run 100% client-side (jsPDF + html2canvas already in package.json).
 *
 * The chapter capture uses a *live* hidden DOM slot (mounted once at the
 * TutorialLayout root) so chapters keep access to the React Router context.
 * See TutorialPDFExporter.jsx for the slot.
 */

import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { CHAPTERS } from "@/tutorial/chaptersData";

// ──────────────────────────────────────────────────────────────────────
// Config
// ──────────────────────────────────────────────────────────────────────
const A4 = { w: 210, h: 297 };
const MARGIN = { top: 15, right: 15, bottom: 18, left: 15 };
const CONTENT = {
  w: A4.w - MARGIN.left - MARGIN.right,
  h: A4.h - MARGIN.top - MARGIN.bottom,
};
const NAVY = "#1A3C5E";
const GOLD = "#C9A84C";

const FORMULAS = [
  {
    name: "Salaire journalier",
    expr: "Salaire_mensuel ÷ 30",
    desc: "Base de calcul vitaliste. La vie ne s'arrête pas le week-end.",
  },
  {
    name: "Jours de vie / an",
    expr: "Total_prélèvements ÷ Salaire_journalier",
    desc: "Unité fondatrice de la théorie vitaliste.",
  },
  {
    name: "Ratio vie-fiscale",
    expr: "Jours_de_vie ÷ 365 × 100",
    desc: "Pourcentage de vie aliéné à la fiscalité chaque année.",
  },
  {
    name: "Dette en vies",
    expr: "Dette_par_tête ÷ Salaire_journalier",
    desc: "Hypothèque future en jours de vie pour les enfants à naître.",
  },
];

function todayStamp() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
}

function frenchDate(d = new Date()) {
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ──────────────────────────────────────────────────────────────────────
// PDF page primitives
// ──────────────────────────────────────────────────────────────────────
function drawCover(pdf) {
  pdf.setFillColor(NAVY);
  pdf.rect(0, 0, A4.w, A4.h, "F");

  // Outer gold frame
  pdf.setDrawColor(GOLD);
  pdf.setLineWidth(0.5);
  pdf.rect(12, 12, A4.w - 24, A4.h - 24);

  // Top eyebrow
  pdf.setTextColor(GOLD);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text("VITAE-PUBLICA™", A4.w / 2, 38, { align: "center" });

  // Big monogram circle
  pdf.setFillColor(GOLD);
  pdf.circle(A4.w / 2, 90, 18, "F");
  pdf.setTextColor(NAVY);
  pdf.setFontSize(22);
  pdf.setFont("times", "bold");
  pdf.text("VP", A4.w / 2, 96, { align: "center" });

  // Title
  pdf.setTextColor("#ffffff");
  pdf.setFont("times", "bold");
  pdf.setFontSize(28);
  pdf.text("Guide complet d'utilisation", A4.w / 2, 140, { align: "center" });

  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(13);
  pdf.setTextColor(GOLD);
  pdf.text(
    "« Les Finances Publiques sont des Tranches de Vies »",
    A4.w / 2,
    154,
    { align: "center" }
  );

  // Date + authors
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  pdf.setTextColor("#ffffff");
  pdf.text(`Édition du ${frenchDate()}`, A4.w / 2, 200, { align: "center" });

  pdf.setDrawColor(GOLD);
  pdf.setLineWidth(0.3);
  pdf.line(A4.w / 2 - 50, 215, A4.w / 2 + 50, 215);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(GOLD);
  pdf.text("Méthodologie & théorie", A4.w / 2, 224, { align: "center" });
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor("#ffffff");
  pdf.setFontSize(11);
  pdf.text("Ahmed ELY Mustapha", A4.w / 2, 230, { align: "center" });

  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(GOLD);
  pdf.setFontSize(10);
  pdf.text("Programmeur", A4.w / 2, 245, { align: "center" });
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor("#ffffff");
  pdf.setFontSize(11);
  pdf.text(
    "ELY Mustapha — PMP · I-PMP · IBM Full Stack Developer",
    A4.w / 2,
    251,
    { align: "center" }
  );

  pdf.setFontSize(8);
  pdf.setTextColor(GOLD);
  pdf.text(
    "Document pédagogique sans valeur juridictionnelle.",
    A4.w / 2,
    A4.h - 20,
    { align: "center" }
  );
}

function drawTOC(pdf, chapterPages) {
  pdf.setFillColor("#fdfaf1");
  pdf.rect(0, 0, A4.w, A4.h, "F");

  pdf.setTextColor(NAVY);
  pdf.setFont("times", "bold");
  pdf.setFontSize(22);
  pdf.text("Table des matières", MARGIN.left, 30);

  pdf.setDrawColor(GOLD);
  pdf.setLineWidth(0.6);
  pdf.line(MARGIN.left, 35, A4.w - MARGIN.right, 35);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);

  let y = 48;
  CHAPTERS.forEach((ch, i) => {
    const num = String(ch.id).padStart(2, "0");
    const startPage = chapterPages[ch.id] || "—";
    const dotted = ".".repeat(60);
    const left = `${num}.  ${ch.title}`;
    const right = `p. ${startPage}    ·    ${ch.duration} min`;

    pdf.setTextColor(NAVY);
    pdf.text(left, MARGIN.left, y);
    pdf.setTextColor("#999");
    pdf.text(dotted, MARGIN.left + 70, y);
    pdf.setTextColor(NAVY);
    pdf.text(right, A4.w - MARGIN.right, y, { align: "right" });

    if (typeof startPage === "number") {
      // jsPDF doesn't expose page targets directly — we stamp a TOC link
      // pointing to the page number using setPage. The link rectangle is
      // approximate but functional in most viewers.
      pdf.link(MARGIN.left, y - 4, A4.w - MARGIN.left - MARGIN.right, 6, {
        pageNumber: startPage,
      });
    }
    y += 11;
    if (i === CHAPTERS.length - 1) {
      y += 4;
      pdf.setFont("helvetica", "italic");
      pdf.setTextColor("#888");
      pdf.text(
        "Annexe : formules vitalistes de référence",
        MARGIN.left,
        y
      );
      pdf.setFont("helvetica", "normal");
    }
  });
}

function drawAnnex(pdf) {
  pdf.setFillColor("#fdfaf1");
  pdf.rect(0, 0, A4.w, A4.h, "F");

  pdf.setTextColor(NAVY);
  pdf.setFont("times", "bold");
  pdf.setFontSize(22);
  pdf.text("Annexe — Formules vitalistes de référence", MARGIN.left, 30);

  pdf.setDrawColor(GOLD);
  pdf.setLineWidth(0.6);
  pdf.line(MARGIN.left, 35, A4.w - MARGIN.right, 35);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor("#555");
  pdf.text(
    "Les quatre formules ci-dessous résument la mécanique de calcul de l'application.",
    MARGIN.left,
    44
  );

  // Header row
  let y = 58;
  pdf.setFillColor(NAVY);
  pdf.rect(MARGIN.left, y - 6, CONTENT.w, 9, "F");
  pdf.setTextColor("#ffffff");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text("Formule", MARGIN.left + 3, y);
  pdf.text("Expression", MARGIN.left + 55, y);
  pdf.text("Description", MARGIN.left + 110, y);

  y += 6;
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(NAVY);
  pdf.setFontSize(10);
  FORMULAS.forEach((f, i) => {
    if (i % 2 === 1) {
      pdf.setFillColor("#f3ece0");
      pdf.rect(MARGIN.left, y, CONTENT.w, 18, "F");
    }
    pdf.setFont("helvetica", "bold");
    pdf.text(f.name, MARGIN.left + 3, y + 8);
    pdf.setFont("courier", "normal");
    pdf.text(f.expr, MARGIN.left + 55, y + 8);
    pdf.setFont("helvetica", "normal");
    const lines = pdf.splitTextToSize(f.desc, 80);
    pdf.text(lines, MARGIN.left + 110, y + 8);
    y += 18;
  });

  // Quote at the bottom
  y = A4.h - 60;
  pdf.setDrawColor(GOLD);
  pdf.setLineWidth(0.4);
  pdf.line(MARGIN.left, y, A4.w - MARGIN.right, y);
  pdf.setFont("times", "italic");
  pdf.setFontSize(11);
  pdf.setTextColor(NAVY);
  pdf.text(
    "« La monnaie n'est qu'une représentation conventionnelle ;",
    A4.w / 2,
    y + 10,
    { align: "center" }
  );
  pdf.text(
    "le temps de vie, lui, est inaliénable. »",
    A4.w / 2,
    y + 16,
    { align: "center" }
  );
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor("#888");
  pdf.text("— Manifeste vitaliste", A4.w / 2, y + 24, { align: "center" });
}

function stampFootersAndPageNumbers(pdf) {
  const total = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    pdf.setPage(i);
    if (i === 1) continue; // skip cover
    pdf.setDrawColor(GOLD);
    pdf.setLineWidth(0.2);
    pdf.line(MARGIN.left, A4.h - 12, A4.w - MARGIN.right, A4.h - 12);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor("#888");
    pdf.text(
      "VITAE-PUBLICA™ — Guide utilisateur · ELY Mustapha PMP·I-PMP·IBM",
      MARGIN.left,
      A4.h - 7
    );
    pdf.text(`Page ${i} / ${total}`, A4.w - MARGIN.right, A4.h - 7, {
      align: "right",
    });
  }
}

// ──────────────────────────────────────────────────────────────────────
// Chapter capture & pagination
// ──────────────────────────────────────────────────────────────────────
async function nextPaint() {
  return new Promise((r) =>
    requestAnimationFrame(() => requestAnimationFrame(r))
  );
}

async function captureNode(node) {
  return html2canvas(node, {
    scale: 1.5,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
    windowWidth: node.scrollWidth,
    windowHeight: node.scrollHeight,
  });
}

function paginateCanvasToPdf(pdf, canvas) {
  // Convert full canvas to slices that fit a single A4 content area.
  const pxPerMm = canvas.width / CONTENT.w;
  const sliceHeightPx = Math.floor(CONTENT.h * pxPerMm);
  let y = 0;
  let firstSlice = true;

  while (y < canvas.height) {
    const remaining = canvas.height - y;
    const thisSlicePx = Math.min(sliceHeightPx, remaining);

    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = thisSlicePx;
    const ctx = slice.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(
      canvas,
      0,
      y,
      canvas.width,
      thisSlicePx,
      0,
      0,
      canvas.width,
      thisSlicePx
    );

    if (!firstSlice) pdf.addPage();
    firstSlice = false;

    const heightMm = thisSlicePx / pxPerMm;
    pdf.addImage(
      slice.toDataURL("image/jpeg", 0.85),
      "JPEG",
      MARGIN.left,
      MARGIN.top,
      CONTENT.w,
      heightMm,
      undefined,
      "FAST"
    );

    y += thisSlicePx;
  }
}

// ──────────────────────────────────────────────────────────────────────
// Main: exportTutorialPDF
//   slot       : ref to the hidden DOM slot (must already be in the tree)
//   setSlotChapter(id)  : Promise that resolves once the slot has rendered the chapter
//   onProgress({percent, label})
// ──────────────────────────────────────────────────────────────────────
export async function exportTutorialPDF({
  slotEl,
  setSlotChapter,
  onProgress,
}) {
  if (!slotEl || typeof setSlotChapter !== "function") {
    throw new Error("PDF export slot not initialised.");
  }

  // Force light theme for predictable rendering
  const html = document.documentElement;
  const wasDark = html.classList.contains("dark");
  if (wasDark) html.classList.remove("dark");

  const pdf = new jsPDF({ format: "a4", unit: "mm", orientation: "portrait" });
  const total = CHAPTERS.length + 3; // cover + TOC + 11 chapters + annex
  let step = 0;
  const tick = (label) => {
    step += 1;
    if (onProgress) onProgress({ percent: Math.round((step / total) * 100), label });
  };

  try {
    // --- Cover ---
    onProgress?.({ percent: 0, label: "Couverture" });
    drawCover(pdf);
    tick("Couverture créée");

    // --- TOC placeholder (we'll fill it later, once we know page counts) ---
    pdf.addPage();
    const tocPageIndex = pdf.internal.getNumberOfPages();
    tick("Table des matières");

    // --- Chapters ---
    const chapterPages = {};
    for (const ch of CHAPTERS) {
      onProgress?.({
        percent: Math.round((step / total) * 100),
        label: `Chapitre ${String(ch.id).padStart(2, "0")} — ${ch.title}`,
      });

      await setSlotChapter(ch.id);
      await nextPaint();
      // small extra delay for any animation/Recharts/Radix mount
      await new Promise((r) => setTimeout(r, 120));

      pdf.addPage();
      chapterPages[ch.id] = pdf.internal.getNumberOfPages();

      const canvas = await captureNode(slotEl);
      paginateCanvasToPdf(pdf, canvas);

      tick(`Chapitre ${ch.id} ajouté`);
    }
    await setSlotChapter(null);

    // --- Annex ---
    pdf.addPage();
    drawAnnex(pdf);
    tick("Annexe ajoutée");

    // --- Fill TOC now that we know each chapter's start page ---
    pdf.setPage(tocPageIndex);
    drawTOC(pdf, chapterPages);

    // --- Footer + page numbers on every page (except cover) ---
    stampFootersAndPageNumbers(pdf);

    const filename = `VITAE-PUBLICA_Tutoriel_Complet_${todayStamp()}.pdf`;
    pdf.save(filename);
    onProgress?.({ percent: 100, label: "Téléchargement…" });
    return filename;
  } finally {
    if (wasDark) html.classList.add("dark");
    try {
      await setSlotChapter(null);
    } catch {
      /* ignore */
    }
  }
}
